import {userStore} from '@js/stores/userStore';

import {showProductModal} from '@js/actions/basketActions';
import {pushError} from '@js/actions/errorManagement';

import {MAP_CLASS, MAP_MODULES} from '@js/modules/maps/mapConstants';


class MapBuilder {
    _requiredClass = null;

    _requiredModules = [];

    _mapClass = null;

    /* Public methods
    ******************** */
    createMap = (mapClass, modules, params) => {
        this._requiredClass = mapClass;
        this._requiredModules = modules;

        return Promise.allSettled(this._loadMapModules())
            .then((results) => (
                results.forEach((result, promiseIndex) => {
                    if (result.status === 'fulfilled') {
                        if (promiseIndex === 0) {
                            this._mapClass = result.value.default;
                        } else if (this._mapClass) {
                            Object.assign(this._mapClass.prototype, result.value.default);
                        }
                    } else if (result.status === 'rejected') {
                        pushError(result.reason);
                    }
                })
            ))
            .then(() => {
                if (!this._mapClass) {
                    return false;
                }

                return new this._mapClass(
                    modules,
                    {
                        ...params,
                        snapToRoads: userStore.featureFlags.map_snap_to_road_enabled,
                        hasMapLimitations: userStore.featureFlags.map_limitations_enabled,
                        mapMaxRasterTilesCount: userStore.featureFlags.map_max_raster_tiles_count,
                        mapMaxVectorTilesCount: userStore.featureFlags.map_max_vector_tiles_count,
                        mapMaxRouteComputationCount: userStore.featureFlags.map_max_route_computation_count,
                        mapMaxIsochrone: userStore.featureFlags.map_max_isochrone,
                        mapMaxDistance: userStore.featureFlags.map_max_distance,
                        // OPTIMIZE: use it for POI mode
                        poiPlanEnabled: userStore.featureFlags.ride_plan_enabled,
                        showProductPopup: showProductModal
                    }
                );
            })
            .catch((error) => {
                pushError(error);
            });
    };

    /* Private methods
    ******************** */
    _loadMapModules = () => {
        const promiseRequests = [];

        this._requiredModules.forEach((module) => {
            if (!Object.values(MAP_MODULES)
                .includes(module)) {
                throw `Unknown module to load: ${module}`;
            }
        });

        if (this._requiredClass === MAP_CLASS.RIDE) {
            promiseRequests.push(import(/* webpackChunkName: "maps-ride" */ '@js/modules/maps/rideMap'));
        } else if (this._requiredClass === MAP_CLASS.SEARCH) {
            promiseRequests.push(import(/* webpackChunkName: "maps-search" */ '@js/modules/maps/searchMap'));
        } else if (this._requiredClass === MAP_CLASS.BUILD) {
            promiseRequests.push(import(/* webpackChunkName: "maps-build" */ '@js/modules/maps/buildMap'));
        } else if (this._requiredClass === MAP_CLASS.EXPLORE) {
            promiseRequests.push(import(/* webpackChunkName: "maps-explore" */ '@js/modules/maps/exploreMap'));
        } else if (this._requiredClass === MAP_CLASS.PLAN) {
            promiseRequests.push(import(/* webpackChunkName: "maps-plan" */ '@js/modules/maps/planMap'));
        } else {
            throw new Error('Try to initialize unknown map class: ' + this._requiredClass);
        }

        if (this._requiredModules.includes(MAP_MODULES.TOOLBAR)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-toolbar" */ '@js/modules/maps/modules/toolbarMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.RIDE_MARKERS)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-ride-markers" */ '@js/modules/maps/modules/rideMarkersMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.RIDE_TRACK)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-ride-track" */ '@js/modules/maps/modules/rideTrackMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.RIDE_POIS)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-ride-pois" */ '@js/modules/maps/modules/ridePoisMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.RIDE_STORE)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-ride-store" */ '@js/modules/maps/modules/rideStoreMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.RIDE_SHARE)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-ride-share" */ '@js/modules/maps/modules/rideShareMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.ELEVATION_COMPUTATION)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-elevation-computation" */ '@js/modules/maps/modules/elevationComputationMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.COMPUTE_ROAD)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-compute-road" */ '@js/modules/maps/modules/computeRoadMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.DRAW_LINE)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-draw-line" */ '@js/modules/maps/modules/drawLineMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.GRAPH)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-graph" */ '@js/modules/maps/modules/graphMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.MARKER_VISIBILITY)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-marker-visibility" */ '@js/modules/maps/modules/markerVisibilityMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.GEOLOCATION)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-geolocation" */ '@js/modules/maps/modules/geolocationMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.ISOCHRONE)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-isochrone" */ '@js/modules/maps/modules/isochroneMap'));
        }
        if (this._requiredModules.includes(MAP_MODULES.HELPER)) {
            promiseRequests.push(import(/* webpackChunkName: "maps-helper" */ '@js/modules/maps/modules/helperMap'));
        }

        return promiseRequests;
    };
}

export default new MapBuilder();
